import type { Comparator } from './sort-by'

import { FALLBACK_CATEGORY_NAME, MISSING_ITEMS_CATEGORY_NAME } from '@shared/domain/categories'

import { Comparators, compareConcat } from './sort-by'

type GetCompareValue<T> = (x: T) => string

export const groupByCategory = <T>(xs: T[], f: GetCompareValue<T>): Record<string, T[]> =>
  xs.reduce<Record<string, T[]>>((p, c) => {
    const categoryName = f(c)
    return {
      ...p,
      [categoryName]: [...(p[categoryName] || []), c],
    }
  }, {})

export const sortInCategories = <T>(
  x: Record<string, T[]>,
  comparators: Comparator<T>[],
): Record<string, T[]> =>
  Object.fromEntries(
    Object.entries(x).map(([category, items]) => [
      category,
      [...items].sort(compareConcat(comparators)),
    ]),
  )

const isFallbackCategory = (a: string) => (a === FALLBACK_CATEGORY_NAME ? 1 : 0)
const sortFallbackCategory: Comparator<string> = (a, b) =>
  Comparators.gte(isFallbackCategory(a), isFallbackCategory(b))

const isMissingItemCategory = (a: string) => (a === MISSING_ITEMS_CATEGORY_NAME ? 1 : 0)
const sortMissingItemsCategory: Comparator<string> = (a, b) =>
  Comparators.gte(isMissingItemCategory(a), isMissingItemCategory(b))

const categorySortOrder = [
  // missing items should be last
  sortMissingItemsCategory,
  // fallback items should be second to last
  sortFallbackCategory,
  // if neither of, sort by string value
  Comparators.gte,
]

export const getSortedCategoryNames = <T>(categories: Record<string, T[]>) =>
  Object.keys(categories).sort(compareConcat(categorySortOrder))
